import { API_ENDPOINTS } from '../config/api'
import { getActiveOrgId } from '../utils/orgUtils'
import type { UnifiedExecutiveContext } from '../types/unifiedExecutiveContext'
import type { ExecutivePosture, ExecutiveStandardMiniCard } from './executiveGovernanceApi'

function withOrganizationScope(url: string): string {
  const orgId = getActiveOrgId()
  if (!orgId) return url
  const sep = url.includes('?') ? '&' : '?'
  return `${url}${sep}organization_id=${encodeURIComponent(orgId)}`
}

function getAuthHeaders(): HeadersInit {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('token') ||
        localStorage.getItem('auth_token') ||
        sessionStorage.getItem('authToken') ||
        localStorage.getItem('accessToken')
      : null
  return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' }
}

async function parseJson(res: Response) {
  const body = await res.json()
  if (!res.ok) throw new Error(body?.message || body?.error || 'Request failed')
  return body?.data ?? body
}

export type EnterpriseTimelineItem = {
  id: string
  at: string
  eventType: string
  severity: string
  summary: string
  relatedStandardCode?: string | null
}

export type EnterpriseHomeSummary = {
  enterpriseUxEnabled: boolean
  generatedAt?: string
  executive: ExecutivePosture | null
  standards: ExecutiveStandardMiniCard[]
  timeline: EnterpriseTimelineItem[]
  blastRadius?: ExecutivePosture['blastRadius']
  scopeByStandard?: Record<string, { informationSystemCount: number; label: string }>
  organizationAssetCount?: number
  hasExecutiveBaseline?: boolean
  hasRuntimeEvents?: boolean
}

export type InformationSystemExecutiveSummary = {
  informationSystemId: string
  hasEnoughHistory: boolean
  pressure: 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL'
  operationalPressure: number
  executiveCriticality: string
  certificationStability: string | null
  readinessTrajectory: string | null
  workflowStages: Array<{ standardCode: string; workflowStage: string }>
  latestRuntimeEvent: {
    id: string
    eventType: string
    severity: string
    at: string
    summary: string
    relatedStandardCode?: string | null
  } | null
  recentRuntimeEvents: Array<{
    id: string
    eventType: string
    severity: string
    at: string
    summary: string
  }>
  sharedControlBlastRadius: Array<{
    controlCode: string
    summary: string
    impactedStandards: string[]
    standardCount: number
    systemCount: number
    severity: string
  }>
  degradedControlCount: number
  blockerCount: number
  staleEvidenceCount: number
  impactedStandards: string[]
  applicableStandards?: string[]
  complianceScope?: Array<{
    standardCode: string
    selfAssessedPercent: number | null
    verifiedPercent: number | null
    verifiedStatus?: string | null
    verifiedSource?: string | null
    available?: boolean
  }>
  explanations: string[]
  recommendedNextActions: string[]
}

export async function fetchInformationSystemsExecutiveSummary(informationSystemIds: string[]) {
  const q = new URLSearchParams()
  if (informationSystemIds.length) {
    q.set('informationSystemIds', informationSystemIds.join(','))
  }
  const base = API_ENDPOINTS.ENTERPRISE_OPERATIONAL_UX_IS_SUMMARY
  const url = q.toString() ? `${base}?${q}` : base
  const res = await fetch(withOrganizationScope(url), { headers: getAuthHeaders() })
  if (res.status === 404) {
    return { systems: [] as InformationSystemExecutiveSummary[], enterpriseUxEnabled: false }
  }
  const data = await parseJson(res) as { systems: InformationSystemExecutiveSummary[] }
  return { systems: data.systems || [], enterpriseUxEnabled: true }
}

export type DataMapFlowOperationalSummary = {
  id: string
  sourceSystemId: string
  targetSystemId: string
  sourceSystemName: string
  targetSystemName: string
  dataCategory: string
  flowType: string
  direction: string
  crossBorderTransfer: boolean
  thirdPartyInvolved: boolean
  significanceTags: string[]
  dataContext: {
    criticalData: boolean
    complianceIndicators: string[]
    inheritedScopeStandards: string[]
    scopeReasoning: string | null
  }
  operationalState: {
    operationalRiskLevel: string | null
    sourcePressure: string | null
    targetPressure: string | null
    certificationStability: string | null
    readinessTrajectory: string | null
    degraded: boolean
    staleEvidence: boolean
    blockers: number
    degradedControls: number
  }
  runtime: {
    latestTopologyEvent: {
      id: string
      eventType: string
      at: string
      summary: string
    } | null
    recentEvents: Array<{ id: string; eventType: string; at: string; summary: string }>
    driftDetected: boolean
  }
  blastRadius: {
    impactedStandards: string[]
    blastHits: InformationSystemExecutiveSummary['sharedControlBlastRadius']
    blastRadiusContribution: number
  }
  workflowStages: Array<{ standardCode: string; workflowStage: string }>
  storytelling: string | null
}

export type DataMapExecutiveSummary = {
  enterpriseUxEnabled: boolean
  generatedAt?: string
  postureStrip: {
    systemsInScope: number
    criticalDataSystems: number
    degradedSystems: number
    highestPressureSystems: number
    staleEvidenceSystems: number
    runtimeInstabilityCount: number
    degradedFlows: number
    crossStandardBlastRadiusCount: number
  }
  explanations: string[]
  flows: DataMapFlowOperationalSummary[]
  systems: InformationSystemExecutiveSummary[]
  topologyEvents: Array<{
    id: string
    eventType: string
    severity: string
    at: string
    summary: string
    relatedInformationSystemId?: string | null
    relatedStandardCode?: string | null
  }>
  blastRadiusImpacts: Array<{
    controlCode: string
    summary: string
    standardCount: number
    systemCount: number
    impactedStandards: string[]
  }>
  organizationTopology?: unknown
}

export async function fetchDataMapExecutiveSummary() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.ENTERPRISE_OPERATIONAL_UX_DATA_MAP_SUMMARY), {
    headers: getAuthHeaders(),
  })
  if (res.status === 404) {
    return { enterpriseUxEnabled: false } as DataMapExecutiveSummary
  }
  if (!res.ok) {
    console.warn('[fetchDataMapExecutiveSummary]', res.status, await res.text().catch(() => ''))
    return {
      enterpriseUxEnabled: true,
      flows: [],
      systems: [],
      postureStrip: undefined,
      explanations: [],
      topologyEvents: [],
      blastRadiusImpacts: [],
    } as DataMapExecutiveSummary
  }
  return parseJson(res) as DataMapExecutiveSummary
}

export type RiskComplianceStandardRow = {
  kind: 'standard'
  standardCode: string
  standardName: string
  hasEnoughHistory: boolean
  certificationRiskLevel: string | null
  readinessTrajectory: string | null
  operationalPressure: number
  pressure: string | null
  selfPercent: number | null
  verifiedPercent: number | null
  degradedControlCount: number
  blockerCount: number
  staleEvidenceCount: number
  workflowStage: string | null
  latestRuntimeEvent: {
    id: string
    eventType: string
    at: string
    summary: string
    severity?: string
  } | null
  recentRuntimeEvents: Array<{ id: string; eventType: string; at: string; summary: string }>
  storytelling: string | null
  isDegradedStandard: boolean
}

export type RiskComplianceExecutiveSummary = {
  enterpriseUxEnabled: boolean
  generatedAt?: string
  executive?: import('./executiveGovernanceApi').ExecutivePosture | null
  postureStrip: {
    certificationRisk: string | null
    degradedWorkflows: number
    unstableControls: number
    blockerConcentration: number
    staleEvidencePressure: number
    runtimeDegradationCount: number
    blastRadiusConcentration: number
    highPressureSystems: number
    degradedStandardsCount: number
  }
  explanations: string[]
  standardRows: RiskComplianceStandardRow[]
  degradedStandards: RiskComplianceStandardRow[]
  unstableWorkflows: Array<{
    informationSystemId: string
    standardCode: string
    workflowStage: string
  }>
  systems: InformationSystemExecutiveSummary[]
  runtimeEvents: DataMapExecutiveSummary['topologyEvents']
  blastRadiusImpacts: DataMapExecutiveSummary['blastRadiusImpacts']
  blastRadius?: import('./executiveGovernanceApi').ExecutivePosture['blastRadius']
}

export type AssetInventoryExecutiveAsset = {
  assetId: number | string
  name: string
  type: string
  environment: string | null
  criticality: string | null
  lifecycleStatus: string | null
  ownerLabel?: string | null
  networkTarget?: string | null
  lastScan?: string | null
  tags?: string[]
  linkedSystemsCount: number
  linkedInformationSystems?: Array<{ informationSystemId: string; name: string }>
  exposure: {
    internetFacing: boolean
    production: boolean
    externallyAccessible: boolean
    exposureLevel: string | null
    indicators: string[]
  }
  complianceApplicability: {
    standardsAffected: string[]
    inheritedScope: string[]
    directScope: string[]
  }
  pressure: {
    band: string | null
    operationalPressure: number
    degradedControls: number
    staleEvidenceCount: number
    blockers: number
  }
  assurance: {
    assuranceLevel: string
    controlHealth: string | null
    telemetryPresent: boolean
  }
  vulnerability: {
    count: number
    criticalCount: number
    severity?: {
      critical: number
      high: number
      medium: number
      low: number
      info: number
    }
    topVulnerabilities: Array<{
      id: string
      title: string
      severity?: string
      cve?: string
      lifecycleState?: string
    }>
  }
  blastRadius: {
    hits: InformationSystemExecutiveSummary['sharedControlBlastRadius']
    impactedStandards: string[]
    contribution: number
  }
  workflow: {
    stages: Array<{ standardCode: string; workflowStage: string }>
    degraded: boolean
  }
  runtime: {
    latestEvent: {
      id: string
      eventType: string
      at: string
      summary: string
    } | null
    recentEvents: Array<{ id: string; eventType: string; at: string; summary: string }>
  }
  storytelling: string | null
  recommendedNextActions: string[]
}

export type AssetInventoryExecutiveSummary = {
  enterpriseUxEnabled: boolean
  generatedAt?: string
  postureStrip: {
    criticalExposureAssets: number
    internetFacingAssets: number
    degradedAssets: number
    staleEvidenceAssets: number
    highPressureAssets: number
    runtimeInstabilityCount: number
    blastRadiusConcentration: number
    continuousAssuranceCoverage: number
    partialAssuranceCoverage: number
  }
  explanations: string[]
  assets: AssetInventoryExecutiveAsset[]
  runtimeEvents: RiskComplianceExecutiveSummary['runtimeEvents']
  blastRadiusImpacts: RiskComplianceExecutiveSummary['blastRadiusImpacts']
  executive?: import('./executiveGovernanceApi').ExecutivePosture | null
}

export async function fetchAssetInventoryExecutiveSummary() {
  const res = await fetch(withOrganizationScope(API_ENDPOINTS.ENTERPRISE_OPERATIONAL_UX_ASSET_INVENTORY_SUMMARY), {
    headers: getAuthHeaders(),
  })
  if (res.status === 404) {
    return { enterpriseUxEnabled: false } as AssetInventoryExecutiveSummary
  }
  return parseJson(res) as AssetInventoryExecutiveSummary
}

export async function fetchRiskComplianceExecutiveSummary(standardCodes: string[] = []) {
  const q = new URLSearchParams()
  if (standardCodes.length) q.set('standards', standardCodes.join(','))
  const base = API_ENDPOINTS.ENTERPRISE_OPERATIONAL_UX_RISK_COMPLIANCE_SUMMARY
  const url = q.toString() ? `${base}?${q}` : base
  const res = await fetch(withOrganizationScope(url), { headers: getAuthHeaders() })
  if (res.status === 404) {
    return { enterpriseUxEnabled: false } as RiskComplianceExecutiveSummary
  }
  return parseJson(res) as RiskComplianceExecutiveSummary
}

export async function fetchUnifiedExecutiveContext(
  scope: 'organization' | 'informationSystem' | 'asset',
  entityId?: string,
  title?: string,
) {
  const q = new URLSearchParams({ scope })
  if (entityId) q.set('entityId', entityId)
  if (title) q.set('title', title)
  const url = `${API_ENDPOINTS.ENTERPRISE_OPERATIONAL_UX_UNIFIED_CONTEXT}?${q}`
  const res = await fetch(withOrganizationScope(url), { headers: getAuthHeaders() })
  if (res.status === 404) {
    return { context: null as UnifiedExecutiveContext | null, enterpriseUxEnabled: false }
  }
  const data = await parseJson(res) as {
    context: UnifiedExecutiveContext
    entity?: AssetInventoryExecutiveAsset
    bundleLoadedAt?: number
  }
  return data
}

export async function fetchEnterpriseHomeSummary(standardCodes: string[]) {
  const q = new URLSearchParams()
  if (standardCodes.length) q.set('standards', standardCodes.join(','))
  const base = API_ENDPOINTS.ENTERPRISE_OPERATIONAL_UX_HOME_SUMMARY
  const url = q.toString() ? `${base}?${q}` : base
  const res = await fetch(withOrganizationScope(url), { headers: getAuthHeaders() })
  return parseJson(res) as EnterpriseHomeSummary
}
